import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Pencil, Plus, Eye, EyeOff } from "lucide-react";
import Seo from "@/components/Seo";

const EMPTY_POST = {
  title: "",
  excerpt: "",
  content: "",
  cover_image: "",
  category: "Thoughts",
  read_minutes: 5,
  published: true,
};

const EMPTY_THOUGHT = {
  entry_date: new Date().toISOString().slice(0, 10),
  quote: "",
  quote_source: "",
  reflection: "",
  published: true,
};

const input =
  "w-full bg-background border border-border rounded-sm px-4 py-3 text-sm focus:outline-none focus-visible:ring-1 focus-visible:ring-primary";
const label = "block text-xs uppercase tracking-widest text-foreground/60 mb-2";

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("thoughts");

  if (loading) return <main className="p-20 text-foreground/60">Loading…</main>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin)
    return (
      <main className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="admin-forbidden">
        <h1 className="font-serif text-4xl">Not an admin</h1>
        <p className="mt-4 text-foreground/70">
          You're signed in as {user.email}, but you don't have admin access.
        </p>
      </main>
    );

  return (
    <main className="max-w-6xl mx-auto px-6 md:px-10 py-16" data-testid="admin-page">
      <Seo title="Admin" path="/admin" noindex />
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/60">
            Admin
          </div>
          <h1 className="font-serif text-5xl md:text-6xl font-light tracking-tighter mt-2">
            The workshop.
          </h1>
        </div>
        <div className="text-sm text-foreground/60">
          Signed in as <span className="text-foreground">{user.email}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-10 border-b border-border/60" data-testid="admin-tabs">
        {[
          { id: "thoughts", label: "Thoughts" },
          { id: "posts", label: "Journal" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`admin-tab-${t.id}`}
            className={`px-5 py-3 text-xs uppercase tracking-[0.2em] border-b-2 transition-colors duration-200 -mb-px ${
              tab === t.id
                ? "border-primary text-primary"
                : "border-transparent text-foreground/60 hover:text-primary"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "thoughts" ? <ThoughtsAdmin /> : <PostsAdmin />}
    </main>
  );
}

/* ----------------- Thoughts admin ----------------- */
function ThoughtsAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_THOUGHT);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/thoughts");
      setItems(data);
    } catch {
      setItems([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setEditingId(null);
    setForm(EMPTY_THOUGHT);
  };

  const startEdit = (t) => {
    setEditingId(t.thought_id);
    setForm({
      entry_date: t.entry_date,
      quote: t.quote || "",
      quote_source: t.quote_source || "",
      reflection: t.reflection || "",
      published: t.published,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.quote && !form.reflection) {
      toast.error("Add a quote or a reflection (or both).");
      return;
    }
    setBusy(true);
    try {
      if (editingId) {
        await api.put(`/admin/thoughts/${editingId}`, form);
        toast.success("Thought updated");
      } else {
        await api.post("/admin/thoughts", form);
        toast.success("Thought published");
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this thought?")) return;
    try {
      await api.delete(`/admin/thoughts/${id}`);
      toast.success("Deleted");
      if (editingId === id) resetForm();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const togglePublished = async (t) => {
    try {
      await api.put(`/admin/thoughts/${t.thought_id}`, { published: !t.published });
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  return (
    <>
      <section className="border border-border rounded-sm p-8 md:p-10 mb-14 bg-secondary/30">
        <div className="flex items-center justify-between mb-8">
          <h2 className="font-serif text-3xl">
            {editingId ? "Edit thought" : "New thought"}
          </h2>
          {editingId && (
            <button
              onClick={resetForm}
              data-testid="thought-cancel-edit"
              className="text-xs uppercase tracking-widest text-foreground/60 hover:text-primary transition-colors"
            >
              Cancel
            </button>
          )}
        </div>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="thought-form">
          <div>
            <label className={label}>Entry date</label>
            <input
              type="date"
              className={input}
              required
              value={form.entry_date}
              onChange={(e) => setForm({ ...form, entry_date: e.target.value })}
              data-testid="thought-input-date"
            />
          </div>
          <div>
            <label className={label}>Quote source (optional)</label>
            <input
              className={input}
              value={form.quote_source}
              onChange={(e) => setForm({ ...form, quote_source: e.target.value })}
              data-testid="thought-input-source"
              placeholder="e.g. Charlie Munger"
            />
          </div>
          <div className="md:col-span-2">
            <label className={label}>Quote (optional)</label>
            <textarea
              className={`${input} min-h-[100px]`}
              value={form.quote}
              onChange={(e) => setForm({ ...form, quote: e.target.value })}
              data-testid="thought-input-quote"
            />
          </div>
          <div className="md:col-span-2">
            <label className={label}>Reflection (optional)</label>
            <textarea
              className={`${input} min-h-[160px]`}
              value={form.reflection}
              onChange={(e) => setForm({ ...form, reflection: e.target.value })}
              data-testid="thought-input-reflection"
            />
          </div>
          <div className="md:col-span-2 flex items-center justify-between">
            <label className="inline-flex items-center gap-3 text-sm">
              <input
                type="checkbox"
                checked={form.published}
                onChange={(e) => setForm({ ...form, published: e.target.checked })}
                data-testid="thought-input-published"
              />
              Published
            </label>
            <button
              type="submit"
              disabled={busy}
              data-testid="thought-submit"
              className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-xs uppercase tracking-[0.2em] hover:bg-primary/90 transition-colors disabled:opacity-60"
            >
              <Plus size={14} strokeWidth={1.5} />
              {editingId ? "Save changes" : "Publish"}
            </button>
          </div>
        </form>
      </section>

      <section>
        <h2 className="font-serif text-3xl mb-6">All thoughts</h2>
        <div className="border border-border rounded-sm divide-y divide-border" data-testid="admin-thoughts-list">
          {items.length === 0 && (
            <div className="p-8 text-foreground/60 text-sm" data-testid="admin-thoughts-empty">
              No thoughts yet — write your first entry above.
            </div>
          )}
          {items.map((t) => (
            <div
              key={t.thought_id}
              data-testid={`admin-thought-row-${t.thought_id}`}
              className="p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-4 md:gap-6"
            >
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-accent">
                  {t.entry_date} · {t.published ? "Live" : "Draft"}
                </div>
                {t.quote && (
                  <div className="font-serif italic text-lg mt-1 line-clamp-1">
                    “{t.quote}”
                  </div>
                )}
                {!t.quote && t.reflection && (
                  <div className="text-sm mt-1 line-clamp-1">{t.reflection}</div>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => togglePublished(t)}
                  data-testid={`admin-thought-toggle-${t.thought_id}`}
                  className="p-2 rounded-full border border-border hover:border-primary hover:text-primary transition-colors"
                >
                  {t.published ? <Eye size={16} strokeWidth={1.5} /> : <EyeOff size={16} strokeWidth={1.5} />}
                </button>
                <button
                  onClick={() => startEdit(t)}
                  data-testid={`admin-thought-edit-${t.thought_id}`}
                  className="p-2 rounded-full border border-border hover:border-primary hover:text-primary transition-colors"
                >
                  <Pencil size={16} strokeWidth={1.5} />
                </button>
                <button
                  onClick={() => remove(t.thought_id)}
                  data-testid={`admin-thought-delete-${t.thought_id}`}
                  className="p-2 rounded-full border border-border hover:border-destructive hover:text-destructive transition-colors"
                >
                  <Trash2 size={16} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

/* ----------------- Posts (journal) admin ----------------- */
function PostsAdmin() {
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState(EMPTY_POST);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/posts");
      setPosts(data);
    } catch {
      setPosts([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setEditingId(null);
    setForm(EMPTY_POST);
  };

  const startEdit = (p) => {
    setEditingId(p.post_id);
    setForm({
      title: p.title,
      excerpt: p.excerpt,
      content: p.content,
      cover_image: p.cover_image || "",
      category: p.category,
      read_minutes: p.read_minutes,
      published: p.published,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editingId) {
        await api.put(`/admin/posts/${editingId}`, form);
        toast.success("Post updated");
      } else {
        await api.post("/admin/posts", form);
        toast.success("Post published");
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post permanently?")) return;
    try {
      await api.delete(`/admin/posts/${id}`);
      toast.success("Deleted");
      if (editingId === id) resetForm();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const togglePublished = async (p) => {
    try {
      await api.put(`/admin/posts/${p.post_id}`, { published: !p.published });
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  return (
    <>
      <section className="border border-border rounded-sm p-8 md:p-10 mb-14 bg-secondary/30">
        <div className="flex items-center justify-between mb-8">
          <h2 className="font-serif text-3xl">
            {editingId ? "Edit essay" : "New essay"}
          </h2>
          {editingId && (
            <button
              onClick={resetForm}
              data-testid="admin-cancel-edit"
              className="text-xs uppercase tracking-widest text-foreground/60 hover:text-primary transition-colors"
            >
              Cancel
            </button>
          )}
        </div>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="admin-form">
          <div className="md:col-span-2">
            <label className={label}>Title</label>
            <input
              className={input}
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="admin-input-title"
            />
          </div>
          <div className="md:col-span-2">
            <label className={label}>Excerpt</label>
            <input
              className={input}
              required
              value={form.excerpt}
              onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
              data-testid="admin-input-excerpt"
            />
          </div>
          <div>
            <label className={label}>Category</label>
            <input
              className={input}
              list="admin-category-suggestions"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              data-testid="admin-input-category"
              placeholder="Choose an existing one or type a new"
            />
            <datalist id="admin-category-suggestions">
              {[...new Set(posts.map((p) => p.category).filter(Boolean))].sort().map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
            <p className="mt-2 text-[11px] text-foreground/50 leading-relaxed">
              Existing categories will autocomplete. Type anything new to create a category on the fly.
            </p>
          </div>
          <div>
            <label className={label}>Read minutes</label>
            <input
              type="number"
              min={1}
              max={60}
              className={input}
              value={form.read_minutes}
              onChange={(e) => setForm({ ...form, read_minutes: Number(e.target.value) })}
              data-testid="admin-input-read-minutes"
            />
          </div>
          <div className="md:col-span-2">
            <label className={label}>Cover image URL</label>
            <input
              className={input}
              value={form.cover_image}
              onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
              data-testid="admin-input-cover"
              placeholder="https://…"
            />
          </div>
          <div className="md:col-span-2">
            <label className={label}>Content — use ## for section headings, • or - for bullets, > for quotes, **bold**, *italic*</label>
            <textarea
              className={`${input} min-h-[240px] font-body`}
              required
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              data-testid="admin-input-content"
              placeholder="## First section\n\nRegular paragraph text.\n\n• bullet one\n• bullet two\n\n> A pulled quote for emphasis."
            />
          </div>
          <div className="md:col-span-2 flex items-center justify-between">
            <label className="inline-flex items-center gap-3 text-sm">
              <input
                type="checkbox"
                checked={form.published}
                onChange={(e) => setForm({ ...form, published: e.target.checked })}
                data-testid="admin-input-published"
              />
              Published
            </label>
            <button
              type="submit"
              disabled={busy}
              data-testid="admin-submit"
              className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-xs uppercase tracking-[0.2em] hover:bg-primary/90 transition-colors disabled:opacity-60"
            >
              <Plus size={14} strokeWidth={1.5} />
              {editingId ? "Save changes" : "Publish"}
            </button>
          </div>
        </form>
      </section>

      <section>
        <h2 className="font-serif text-3xl mb-6">All essays</h2>
        <div className="border border-border rounded-sm divide-y divide-border" data-testid="admin-posts-list">
          {posts.length === 0 && (
            <div className="p-8 text-foreground/60 text-sm" data-testid="admin-posts-empty">
              No essays yet — write the first one above.
            </div>
          )}
          {posts.map((p) => (
            <div
              key={p.post_id}
              data-testid={`admin-post-row-${p.post_id}`}
              className="p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-4 md:gap-6"
            >
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-accent">
                  {p.category} · {p.read_minutes} min · {p.published ? "Live" : "Draft"}
                </div>
                <div className="font-serif text-xl mt-1 line-clamp-1">{p.title}</div>
                <div className="text-sm text-foreground/60 mt-1 line-clamp-1">
                  /{p.slug}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => togglePublished(p)}
                  data-testid={`admin-toggle-${p.post_id}`}
                  className="p-2 rounded-full border border-border hover:border-primary hover:text-primary transition-colors"
                >
                  {p.published ? <Eye size={16} strokeWidth={1.5} /> : <EyeOff size={16} strokeWidth={1.5} />}
                </button>
                <button
                  onClick={() => startEdit(p)}
                  data-testid={`admin-edit-${p.post_id}`}
                  className="p-2 rounded-full border border-border hover:border-primary hover:text-primary transition-colors"
                >
                  <Pencil size={16} strokeWidth={1.5} />
                </button>
                <button
                  onClick={() => remove(p.post_id)}
                  data-testid={`admin-delete-${p.post_id}`}
                  className="p-2 rounded-full border border-border hover:border-destructive hover:text-destructive transition-colors"
                >
                  <Trash2 size={16} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
